import { Space, Alert, Input, Checkbox, Row, Col, Button, Typography, Form as AntdForm } from "antd";
import { useForm } from "antd/es/form/Form";
import qs from "qs";
import React, { useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAuth, SignUp as ISignUp } from "../../../../../context/auth";
import { login, password, firstName, lastName, phoneNumber } from '../../../../../utils/inputRules';
import styles from '../../index.module.less';
import { formatPhoneNumber } from '../../../../../utils/formatPhoneNumber';

const SignUpForm = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { authorized, signUp, loading, error, clearResponseError } = useAuth();
    const [form] = useForm();

    useEffect(() => {
      if (authorized) {
        navigate('/home', { replace: true });
      }
    }, [authorized]);

    useEffect(() => {
      clearResponseError();
    }, []);

    const handleSignUp = (values: ISignUp) => {
      const { ref } = qs.parse(location.search.replace('?', '')) as { [key: string]: string | undefined };

      signUp({ ...values, ref }).then(() => {
        navigate('/email-verification', { replace: true });
      });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.keyCode === 32) {
        e.preventDefault();
      }
    };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhoneNumber = formatPhoneNumber(e.target.value);

    form.setFieldsValue({ phoneNumber: formattedPhoneNumber });
  };

    return (
      <Space direction="vertical" size={14} style={{ padding: "0 12px", overflowY: "auto" }}>
        <AntdForm form={form} layout="vertical" onFinish={handleSignUp} className={styles.form} autoComplete="off">
          {error ? (
            <AntdForm.Item>
              <Alert onClose={clearResponseError} message={error} type="error" closable showIcon />
            </AntdForm.Item>
          ) : null}

          <AntdForm.Item
            name="firstName"
            label="First Name"
            rules={firstName}
            className="label-gray"
            style={{ marginBottom: '12px' }}
          >
            <Input
              size="large"
              placeholder="Enter First Name"
              autoFocus
            />
          </AntdForm.Item>

          <AntdForm.Item
            name="lastName"
            label="Last Name"
            rules={lastName}
            className="label-gray"
            style={{ marginBottom: '12px' }}
          >
            <Input
              size="large"
              placeholder="Enter Last Name"
            />
          </AntdForm.Item>

          <AntdForm.Item
            name="phoneNumber"
            label="Phone Number"
            rules={phoneNumber}
            className="label-gray"
            style={{ marginBottom: '12px' }}
          >
            <Input
              size="large"
              placeholder="Enter Phone Number"
              onChange={handlePhoneNumberChange}
            />
          </AntdForm.Item>

          <AntdForm.Item
            name="email"
            label="Email"
            rules={login}
            className="label-gray"
            style={{ marginBottom: '12px' }}
          >
            <Input
              size="large"
              name="email"
              placeholder="Enter Email"
              onKeyDown={handleKeyDown}
            />
          </AntdForm.Item>

          <AntdForm.Item
            name="password"
            label="Password"
            rules={password}
            extra="Min 8 characters"
            hasFeedback
            className="label-gray"
            style={{ marginBottom: '12px' }}
          >
            <Input.Password
              size="large"
              name="password"
              placeholder="Enter Password"
              onKeyDown={handleKeyDown}
            />
          </AntdForm.Item>

          <AntdForm.Item name="terms" valuePropName="checked">
            <Checkbox className="color-gray checkbox-large">
              By clicking, I accept{' '}
              <Link to="https://mortgagemaker.ai/terms-of-service/" target="_blank">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="https://mortgagemaker.ai/privacy-policy/" target="_blank">
                Privacy Policy
              </Link>
            </Checkbox>
          </AntdForm.Item>

          <AntdForm.Item shouldUpdate noStyle>
            {({ getFieldsValue, getFieldsError }) => {
              // eslint-disable-next-line @typescript-eslint/no-shadow
              const { terms, firstName, lastName, phoneNumber, email, password: passwordValue } = getFieldsValue();
              const formIsComplete =
                terms && firstName && lastName && phoneNumber && email && passwordValue
                && getFieldsError().every((field) => !field.errors.length);

              return (
                <Row gutter={24}>
                  <Col span={24}>
                    <Button
                      size="large"
                      type="primary"
                      htmlType="submit"
                      block
                      loading={loading}
                      disabled={!formIsComplete}
                    >
                      Create My Account
                    </Button>
                  </Col>
                </Row>
              );
            }}
          </AntdForm.Item>
        </AntdForm>

        <div className={styles.textBox}>
          <Typography.Text className={styles.text}>
            Already have an account?{' '}
            <Link to="/sign-in" style={{ color: '#1E0C68', cursor: 'pointer' }}>
              Sign In
            </Link>
          </Typography.Text>
        </div>
      </Space>
    );
  };

  export default SignUpForm;
